import { useLocale, useParamsKey, useUserShow, useResourceShow } from '@koopajs/react';
import { Typography, Box, Stack, Button, Breadcrumbs, Link, Divider } from '@mui/material';
import { Helmet } from 'react-helmet';
import { IResolution } from 'types';
import { Link as RouterLink } from 'react-router-dom';
import { DocumentList } from 'components/DocumentList';
import { useRef } from 'react';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getCurrentUserRoles } from 'utils/getCurrentUserRoles';
import RichTextReadOnly from 'components/temp/RichTextMultiLine/RichTextReadOnly';
import { EventsTimeline } from 'components/EventsTimeline';
import { SignatureList } from 'components/Signature/SignatureList';
import { generateResolutionTimeline } from 'utils/generateResolutionTimeline';
import { RenderPrintButton } from 'components/RenderPrintButton';
import { ViewResolutionPrintVersion } from 'components/Dialogs/ViewResolutionPrintVersion';
import { getSignaturesWithTitles } from 'utils/getSignaturesWithTitles';
import { PageContainer } from 'components/temp/PageContainer';
import { WrittenResolutionDialogDetailsCard } from 'components/Resolutions/WrittenResolutionDialogDetailsCard';
import { PanoramaDocumentWrapper } from 'components/PanoramaDocumentWrapper';

interface IWrittenResolutionProps {
  resolution: IResolution;
}

export const WrittenResolutionView: React.FC<IWrittenResolutionProps> = (props) => {
  const resolutionId = useParamsKey('id');

  const { resource: resolution } = useResourceShow<IResolution>({
    path: '/resolutions',
    id: resolutionId
  });

  const { t } = useLocale();
  const keyPrefix = 'WrittenResolution';
  const printComponentRef = useRef(null);

  const { user: currentUser } = useUserShow();

  const isResolutionDraft = resolution?.state === 'draft';
  const isResolutionSealed = resolution?.state === 'sealed';

  const isCurrentUserOwner = currentUser?.id === resolution?.$ownedBy;

  const isUserWrittenResolutionCreatorRole = getCurrentUserRoles({
    activeCommitteeMembers: resolution?.activeCommitteeMembers,
    userId: currentUser?.id
  }).includes('createResolutionProjects');

  const userHasEditAccess = (isCurrentUserOwner || isUserWrittenResolutionCreatorRole) && !isResolutionSealed;

  const timelineEvents = generateResolutionTimeline(resolution);

  const signaturesWithTitles = getSignaturesWithTitles(resolution);

  return (
    <PageContainer sxChildren={{ padding: '24px' }} className="rr-block">
      <Helmet>
        <title>{`${t(keyPrefix + '.titleView')} - Panorama`}</title>
      </Helmet>
      <Breadcrumbs aria-label="breadcrumb" sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Link
          underline="hover"
          color="inherit"
          to={isResolutionDraft ? '/tasks/to-review' : '/tasks/to-sign'}
          sx={{ cursor: 'pointer' }}
          component={RouterLink}
        >
          {t(`common:navigation.${isResolutionDraft ? 'toAdopt' : 'toSign'}`)}
        </Link>
        <Typography color="text.primary"> {t(keyPrefix + '.titleView')}</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'none' }}>
        <ViewResolutionPrintVersion resolution={resolution} ref={printComponentRef} />
      </Box>
      <Box sx={{ mx: { lg: 9 } }}>
        {/* TITLE AND BUTTONS */}
        <Box sx={{ mt: { sm: 4 }, mb: 2 }}>
          <Stack flexDirection={{ sm: 'row' }} justifyContent="space-between">
            {/* title + mobile back */}
            <Stack flexDirection="row" alignItems="center">
              <Button
                component={RouterLink}
                to={isResolutionDraft ? '/tasks/to-review' : '/tasks/to-sign'}
                variant="outlined"
                sx={{ minWidth: 0, px: '5px', display: { sm: 'none' }, mr: '14px' }}
                aria-label={t('common:labelBack')}
              >
                <ArrowBackIcon sx={{ color: 'primary.main' }} />
              </Button>
              <Typography
                variant="h1"
                component="h1"
                sx={{ display: 'inline-block', fontWeight: 500, fontSize: '20px', mr: 'auto' }}
              >
                {t(keyPrefix + '.titleView')}
              </Typography>
            </Stack>

            {userHasEditAccess && (
              <Stack
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="flex-end"
                sx={{ mt: { xs: 3, sm: 1 } }}
              >
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={{
                    pathname: `/resolutions/${resolution?.id}/edit`
                  }}
                  sx={{ color: 'primary.main' }}
                  data-cy="resolution-review_edit-resolution-button"
                >
                  {t('common:labelEdit')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* MAIN CARD */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', my: 3, gap: 5 }}>
          <PanoramaDocumentWrapper>
            {resolution && <WrittenResolutionDialogDetailsCard resolution={resolution} />}
            <Divider />

            <Typography
              variant="body1"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: '0px' }}
              data-cy="resolution-preview_resolution-title"
            >
              {resolution?.title}
            </Typography>
            {resolution?.notes && (
              <>
                <Typography variant="body2" component="div">
                  <RichTextReadOnly value={resolution.notes} />
                </Typography>
              </>
            )}

            {resolution?.documentsIds && resolution?.documentsIds?.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ mb: '4px', opacity: 0.6 }}>
                  {t('common:relatedDocumentationLabel')}
                </Typography>
                <DocumentList path={`/resolutions/${resolution.id}/documents`} />
              </Box>
            )}

            {signaturesWithTitles && (
              <>
                <Divider />
                <SignatureList signatures={signaturesWithTitles} />
              </>
            )}
          </PanoramaDocumentWrapper>
        </Box>

        {/* TIMELINE */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <EventsTimeline timelineEvents={timelineEvents} />
        </Box>
        {/* FOOTER */}
        <Stack
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={{ xs: 'column-reverse', sm: 'row' }}
          gap={2}
        >
          <Stack
            flexDirection="row"
            gap={2}
            alignItems="center"
            justifyContent={{ xs: 'space-between' }}
            width={{ xs: '100%', sm: 'auto' }}
          >
            {/* mobile back */}
            <Button
              component={RouterLink}
              to={isResolutionDraft ? '/tasks/to-review' : '/tasks/to-sign'}
              variant="outlined"
              sx={{ minWidth: 0, px: '5px', display: { sm: 'none' } }}
              aria-label={t('common:labelBack')}
            >
              <ArrowBackIcon sx={{ color: 'primary.main' }} />
            </Button>

            {/* mobile print */}
            <RenderPrintButton
              componentRef={printComponentRef}
              variant="outlined"
              sx={{ display: { sm: 'none' } }}
            />
          </Stack>

          <Stack
            direction="row"
            gap={2}
            alignItems="flex-start"
            sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
          >
            {/* desktop back */}
            <Button
              component={RouterLink}
              to={isResolutionDraft ? '/tasks/to-review' : '/tasks/to-sign'}
              variant={userHasEditAccess ? undefined : 'contained'}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {t('common:labelBack')}
            </Button>
            <Stack
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 2, sm: 3 }}
              sx={{ mt: { xs: 3, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}
            >
              {/* desktop print */}
              <RenderPrintButton
                componentRef={printComponentRef}
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </PageContainer>
  );
};
